import {useMemo} from 'react'
import {selectOnboardingOccupancies} from '../../reducers/selectors'
import {useSelector} from 'react-redux'
import {createSelector} from 'reselect'
import {isResidentActive, selectRichResidentObjectsFromAppState} from '../../Utils/residentUtils'

const makeSelectResidentsByManyOccupancies = occupancyIds =>
  createSelector([selectRichResidentObjectsFromAppState], residents => {
    return residents.filter(r => occupancyIds.includes(r.occupancy.id) && isResidentActive(r))
  })

// this function does what `useGetOccupancyWithResidentAndUnitData.jsx` does but for all onboarding occupancies
function useGetOnboardingOccupanciesWithResidentAndUnitData() {
  const onboardingOccupancies = useSelector(selectOnboardingOccupancies)
  const unitsLookup = useSelector(state => state.Units.unitsLookup)

  // create a residents selector dependent on which occupancies are onboarding
  // because selectOnboardingOccupancies is a re-Selector, it will only change when occupancies change
  const residentsSelector = useMemo(() => {
    return makeSelectResidentsByManyOccupancies(onboardingOccupancies.map(o => o.id))
  }, [onboardingOccupancies])

  const relevantResidents = useSelector(residentsSelector)

  // remap our array of residents to an easy lookup for convenience
  const residentsByOccupancy = useMemo(
    () =>
      relevantResidents.reduce((agr, r) => {
        if (!agr[r.occupancy.id]) {
          agr[r.occupancy.id] = []
        }
        agr[r.occupancy.id].push(r)
        return agr
      }, {}),
    [relevantResidents],
  )

  return useMemo(
    () =>
      onboardingOccupancies.map(o => {
        return {
          ...o,
          residents: residentsByOccupancy[o.id] || [], // it's possible the occupancy has no residents?
          unit: unitsLookup[o.occupancyUnitId],
        }
      }),
    [onboardingOccupancies, residentsByOccupancy, unitsLookup],
  )
}

export default useGetOnboardingOccupanciesWithResidentAndUnitData
